import {
  createContext, useContext, useState, ReactNode, useMemo,
} from 'react';

interface ApplicantFilterDrawerContextType {
  isOpen: boolean;
  toggleDrawer: () => void;
  openDrawer: () => void;
  closeDrawer: () => void;
}

export const ApplicantFilterDrawerContext = createContext<ApplicantFilterDrawerContextType | undefined>(undefined);

/**
 * Provides filter drawer state for the applicant list inside Mission Control.
 * Consumed by both TalentSearchFilters (to override its internal useBjModal) and
 * CompanyApplicantFloatingNavBar (filter button).
 */
export const ApplicantFilterDrawerProvider = ({ children }: { children: ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleDrawer = () => setIsOpen((prev) => !prev);
  const openDrawer = () => setIsOpen(true);
  const closeDrawer = () => setIsOpen(false);

  const value = useMemo(() => ({
    isOpen,
    toggleDrawer,
    openDrawer,
    closeDrawer,
  }), [isOpen]);

  return (
    <ApplicantFilterDrawerContext.Provider value={value}>
      {children}
    </ApplicantFilterDrawerContext.Provider>
  );
};

/**
 * Consumes ApplicantFilterDrawerContext.
 * Returns undefined when used outside the provider so callers can opt-in gracefully.
 */
export const useApplicantFilterDrawer = () => useContext(ApplicantFilterDrawerContext);
